import api from './api';

const extractSeoKeywords = (data: any): string[] => {
  const keywords = data?.data?.keywords;
  if (!keywords) return [];

  let list: any[] = [];
  if (Array.isArray(keywords.seo)) {
    list = [...list, ...keywords.seo];
  }
  if (Array.isArray(keywords.gmb)) {
    list = [...list, ...keywords.gmb];
  }
  if (Array.isArray(keywords)) {
    list = [...list, ...keywords];
  }

  return list
    .filter((k: any) => k.is_selected || k.is_active)
    .map((k: any) => k.keyword)
    .filter(Boolean);
};

export interface StrategyOverview {
  selectedKeywords: number;
  goalsAchieved: number;
  totalGoals: number;
  activeGoals: number;        // BRD: goals with numeric targets (excludes serp-features)
  notApplicableGoals: number; // BRD: serp-features = Captured Visibility (no fixed target)
  completionRate: number;
  actionItems: number;
  dueActionItems: number;
  lockedUntil: string | null;
}

export interface AsIsMetrics {
  avgPosition: string;
  organicTraffic: string;
  serpVisibility: string;
  domainAuthority: string;
}

export interface CompetitionData {
  yourPosition: number | null;
  marketShare: string;
  actualCompetitorsCount?: number;
  topCompetitors: Array<{ name: string; rank: number; score: number }>;
}

export interface GoalItem {
  goal: string;
  target: string;
  progress: number;
  isPrimary: boolean;
  isPaused?: boolean;
  isNotApplicable?: boolean; // BRD: serp-features has no fixed target
  goal_type?: string;
  target_type?: string;
}

export interface ActionCategory {
  category: string;
  tasks: number;
  completed: number;
  color: string;
  dueThisWeek: number;
}

export interface DashboardStatus {
  keywords_locked: boolean;
  dashboard_ready: boolean;
  locked_until: string | null;
  sections: {
    keywords: { count: number; locked: boolean; status: 'complete' | 'pending' };
    asis: { available: boolean; age_days: number | null; status: 'complete' | 'pending' };
    goals: { count: number; status: 'complete' | 'pending' };
    action_plan: { count: number; status: 'complete' | 'pending' };
  };
  next_step: {
    step: string;
    title: string;
    description: string;
    action: string | null;
    route: string | null;
  };
}

class StrategyService {
  async getStrategyOverview(userId: string): Promise<StrategyOverview> {
    const [keywordData, goalsData, actionPlanData, lockData] = await Promise.all([
      this.getKeywordCount(userId),
      this.getGoalsProgress(userId),
      this.getActionItemsCount(userId),
      this.getKeywordLockDate(userId)
    ]);

    return {
      selectedKeywords: keywordData.count,
      goalsAchieved: goalsData.achieved,
      totalGoals: goalsData.total,
      activeGoals: goalsData.activeGoals,
      notApplicableGoals: goalsData.notApplicableGoals,
      completionRate: goalsData.completionRate,
      actionItems: actionPlanData.total,
      dueActionItems: actionPlanData.due,
      lockedUntil: lockData.lockedUntil
    };
  }

  async getAsIsMetrics(userId: string): Promise<AsIsMetrics> {
    const profile = await api.get<any>(`/profile/${userId}`);
    const siteUrl = profile.data?.website_url || profile.website_url;

    let trackedKeywords: string[] = [];
    try {
      const data = await api.get<any>(`/v2/optimization/keyword-planner/${userId}`);
      trackedKeywords = extractSeoKeywords(data);
    } catch { }

    const data = await api.post<any>(`/asis/summary`, { user_id: userId, site_url: siteUrl, tracked_keywords: trackedKeywords });
    
    const kw = data.data?.keywords_performance || {};
    const traffic = data.data?.organic_traffic || {};
    const serp = data.data?.serp_features || {};
    const comp = data.data?.competitor_rank || {};

    return {
      avgPosition: kw.avg_position ? `${kw.avg_position.toFixed(1)}` : 'N/A',
      organicTraffic: traffic.total_clicks ?
        (traffic.total_clicks >= 1000
          ? `${(traffic.total_clicks / 1000).toFixed(1)}K/mo`
          : `${traffic.total_clicks}/mo`)
        : 'N/A',
      serpVisibility: serp.features_present ? `${((serp.features_present.length / 6) * 100).toFixed(0)}%` : 'N/A',
      domainAuthority: comp.your_visibility_score ? `${comp.your_visibility_score}/100` : 'N/A'
    };
  }

  async getCompetitionData(userId: string): Promise<CompetitionData> {
    const profile = await api.get<any>(`/profile/${userId}`);
    const siteUrl = profile.data?.website_url || profile.website_url;

    let trackedKeywords: string[] = [];
    try {
      const data = await api.get<any>(`/v2/optimization/keyword-planner/${userId}`);
      trackedKeywords = extractSeoKeywords(data);
    } catch { }

    const data = await api.post<any>(`/asis/summary`, { user_id: userId, site_url: siteUrl, tracked_keywords: trackedKeywords });
    
    const comp = data.data?.competitor_rank || {};

    return {
      yourPosition: comp.your_rank !== undefined ? comp.your_rank : null,
      marketShare: comp.market_share ? `${comp.market_share}%` : 'N/A',
      actualCompetitorsCount: comp.actual_competitors_count !== undefined ? comp.actual_competitors_count : (comp.rankings || []).length,
      topCompetitors: (comp.rankings || []).map((c: any) => ({
        name: c.domain,
        rank: c.rank,
        score: c.visibility_score
      }))
    };
  }

  async getGoalsSnapshot(userId: string): Promise<GoalItem[]> {
    const data = await api.get<any>(`/strategy/goals?user_id=${userId}`);
    const goals = data.data?.goals || [];
    return goals.slice(0, 6).map((g: any) => {
      // BRD: serp-features is Captured Visibility — active tracking, never paused.
      // Only truly paused goals (if any) should carry isPaused = true.
      const isPaused = g.goal_type !== 'serp-features' && (g.target_type === 'paused' || g.is_paused);
      const isNotApplicable = g.target_type === 'not_applicable' || g.goal_type === 'serp-features';
      return {
        goal: g.goal_type.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        target: g.target_value + ' ' + g.unit,
        progress: g.progress_percentage || 0,
        isPrimary: false,
        isPaused: isPaused,
        isNotApplicable: isNotApplicable,
        goal_type: g.goal_type,
        target_type: g.target_type,
      };
    });
  }

  async getActionPlanSnapshot(userId: string): Promise<ActionCategory[]> {
    const data = await api.get<any>(`/action-plan/${userId}`);
    const tasks = data.data?.tasks || [];

    // Deduplicate tasks by category + parameter_group + sub_parameter
    const map = new Map<string, any>();
    for (const t of tasks) {
      const key = `${t.category}_${t.parameter_group}_${t.sub_parameter || t.title || t.task_title}`;
      if (!map.has(key)) {
        map.set(key, t);
      }
    }
    const dedupedTasks = Array.from(map.values());

    const categories: Array<{ key: 'onpage' | 'offpage' | 'technical'; label: string; color: string }> = [
      { key: 'onpage', label: 'On Page', color: 'purple' },
      { key: 'offpage', label: 'Off Page', color: 'green' },
      { key: 'technical', label: 'Core Vitals', color: 'blue' },
    ];

    return categories.map(({ key, label, color }) => {
      const catTasks = dedupedTasks.filter((t: any) => t.category === key);
      const completed = catTasks.filter((t: any) => t.status === 'completed').length;
      const dueThisWeek = catTasks.filter((t: any) => t.status === 'not_started').length;
      return {
        category: label,
        tasks: catTasks.length,
        completed,
        color,
        dueThisWeek
      };
    });
  }

  async getDashboardStatus(userId: string): Promise<DashboardStatus> {
    const data = await api.get<any>(`/strategy/dashboard-status/${userId}`);
    return data.data;
  }

  private async getKeywordCount(userId: string) {
    const data = await api.get<any>(`/v2/optimization/keyword-planner/${userId}`);
    const selectedCount = extractSeoKeywords(data).length;
    return { count: selectedCount };
  }

  private async getGoalsProgress(userId: string) {
    const data = await api.get<any>(`/strategy/goals?user_id=${userId}`);
    const goals = data.data?.goals || [];
    // BRD: Captured Visibility (serp-features) is a tracking metric with no fixed target
    const notApplicableGoals = goals.filter((g: any) =>
      g.target_type === 'not_applicable' || g.goal_type === 'serp-features'
    ).length;
    const activeGoals = goals.length - notApplicableGoals;
    const total = goals.length; // Total: 6
    const achieved = goals
      .filter((g: any) => g.goal_type !== 'serp-features' && g.target_type !== 'not_applicable')
      .filter((g: any) => g.progress_percentage >= 100).length;
    const completionRate = activeGoals > 0 ? Math.round((achieved / activeGoals) * 100) : 0;
    return { achieved, total, activeGoals, notApplicableGoals, completionRate };
  }

  private async getActionItemsCount(userId: string) {
    try {
      const data = await api.get<any>(`/action-plan/${userId}`);
      const tasks = data.data?.tasks || [];

      const map = new Map<string, any>();
      for (const t of tasks) {
        const key = `${t.category}_${t.parameter_group}_${t.sub_parameter || t.title || t.task_title}`;
        if (!map.has(key)) {
          map.set(key, t);
        }
      }
      const dedupedTasks = Array.from(map.values());

      const total = dedupedTasks.length;
      const due = dedupedTasks.filter((t: any) => t.status === 'not_started').length;
      return { total, due };
    } catch {
      return { total: 0, due: 0 };
    }
  }

  private async getKeywordLockDate(userId: string) {
    try {
      const data = await api.get<any>(`/keywords/universe/${userId}`);
      const lockedUntil = data.data?.locked_until || null;
      return { lockedUntil };
    } catch {
      return { lockedUntil: null };
    }
  }
}

export const strategyService = new StrategyService();
export default strategyService;
